/**
 * templatePromptPreview — utility puro para renderizar el preview del
 * prompt que el SectionDraftAgent recibiría al generar una sección
 * específica de la plantilla (Doc 24 §3 Fase 12).
 *
 * Shape v2 (Doc 24):
 *   - doc-level: `i18n.{lang}.{name, description, purpose, tone_audience,
 *     ai_global_instructions: string[]}`.
 *   - section structural: `section_id, type, order, context_sections[],
 *     table_config?`.
 *   - section i18n: `title, description?, placeholder?, help_text?,
 *     ai_instructions: DetailedAiInstructions {purpose, must_include[],
 *     must_avoid[], examples: {good, bad: {content, explanation}}}`.
 *
 * El preview NO incluye RAG retrieval, project context, ni system prompts
 * runtime — solo lo que el template author DECLARA. Su propósito es que
 * el author vea cómo lucirá el prompt y pueda iterar antes de publicar.
 *
 * El builder es puro (sin React, sin i18next runtime) — los headers van
 * en español o inglés según el `locale` parámetro. Esto evita acoplar
 * al `t()` de react-i18next y permite usar el builder en tests, jobs o
 * cualquier otro contexto.
 */

import type {
  ArtifactDefinition,
  ArtifactI18nSection,
  ArtifactStructuralSection,
  DetailedAiInstructions,
} from '../api/templates'
import type { SupportedLocale } from '../i18n/config'

export interface PromptPreviewInput {
  definition: ArtifactDefinition
  sectionId: string
  locale: SupportedLocale
}

/** Coerciona `ai_global_instructions` (que puede ser `string[]` v2 o
 * `string` legacy v1) a `string[]`. */
function coerceList(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value
  if (typeof value === 'string' && value.trim().length > 0) return [value]
  return []
}

/** Sanitiza el texto de un bullet — strip de heading markers de markdown
 * al inicio (`#`, `##`, …). Sin esto, un author que escribe
 * `## Identificación` en `must_include` produce `- ## Identificación`
 * en el prompt, que el renderer GFM (`marked`) interpreta como un H2
 * ANIDADO dentro del `<li>` y se ve gigante. Algunos templates seed
 * tienen ese patrón heredado; sanitizar aquí evita migrar data y
 * defiende contra el mismo error si un tenant clona y lo replica.
 *
 * Solo strip al inicio: `**bold**` u otros markdown inline son válidos
 * dentro de bullets y se preservan tal cual. */
function sanitizeBulletItem(item: string): string {
  return item.replace(/^\s*#{1,6}\s+/, '').trim()
}

/** Headers del preview localizados (es/en). Hardcoded para no depender
 * de i18next dentro de un módulo puro. */
const HEADERS: Record<
  SupportedLocale,
  Record<
    | 'doc_rules'
    | 'tone_audience'
    | 'doc_purpose'
    | 'injected_context'
    | 'section_purpose'
    | 'must_include'
    | 'must_avoid'
    | 'example_good'
    | 'example_bad'
    | 'why_bad'
    | 'no_section'
    | 'context_from_section'
    | 'no_content_declared',
    string
  >
> = {
  es: {
    doc_rules: '## Reglas generales del documento',
    tone_audience: '## Tono y audiencia',
    doc_purpose: '## Propósito del documento',
    injected_context: '## Contexto inyectado de secciones previas',
    section_purpose: '## Propósito de esta sección',
    must_include: '## Debe incluir',
    must_avoid: '## Debe evitar',
    example_good: '## Ejemplo bueno',
    example_bad: '## Ejemplo a evitar',
    why_bad: '**Por qué es malo:**',
    no_section: '_(sección no encontrada)_',
    context_from_section: '← contenido de la sección',
    no_content_declared:
      '_(esta sección no tiene contenido declarado por el author — la IA generará a partir del contexto del proyecto)_',
  },
  en: {
    doc_rules: '## General document rules',
    tone_audience: '## Tone and audience',
    doc_purpose: '## Document purpose',
    injected_context: '## Injected context from previous sections',
    section_purpose: '## Purpose of this section',
    must_include: '## Must include',
    must_avoid: '## Must avoid',
    example_good: '## Good example',
    example_bad: '## Example to avoid',
    why_bad: '**Why it is bad:**',
    no_section: '_(section not found)_',
    context_from_section: '← content from section',
    no_content_declared:
      '_(this section has no author-declared content — the AI will generate from project context)_',
  },
}

/** Encuentra el structural por section_id, recorriendo el árbol completo. */
function findStructural(
  sections: ArtifactStructuralSection[],
  sectionId: string,
): ArtifactStructuralSection | null {
  for (const s of sections) {
    if (s.section_id === sectionId) return s
    if (s.children && s.children.length > 0) {
      const found = findStructural(s.children, sectionId)
      if (found) return found
    }
  }
  return null
}

/** Encuentra el bloque i18n localizado de una sección. */
function findI18nSection(
  definition: ArtifactDefinition,
  sectionId: string,
  locale: SupportedLocale,
): ArtifactI18nSection | null {
  const sections = definition.i18n[locale]?.sections ?? []
  return sections.find((s) => s.section_id === sectionId) ?? null
}

/** Normaliza `ai_instructions` a `DetailedAiInstructions` (Doc 24 §2.5).
 * Soporta v1 legacy `Record<string, string>` extrayendo solo `purpose`. */
function normalizeAiInstructions(
  value: DetailedAiInstructions | Record<string, string> | undefined,
): Required<DetailedAiInstructions> {
  const empty: Required<DetailedAiInstructions> = {
    purpose: null,
    must_include: [],
    must_avoid: [],
    examples: null,
  }
  if (!value || typeof value !== 'object') return empty
  const v2 = value as DetailedAiInstructions
  if (
    Array.isArray(v2.must_include) ||
    Array.isArray(v2.must_avoid) ||
    (v2.examples && typeof v2.examples === 'object')
  ) {
    return {
      purpose: v2.purpose ?? null,
      must_include: v2.must_include ?? [],
      must_avoid: v2.must_avoid ?? [],
      examples: v2.examples ?? null,
    }
  }
  const legacy = value as Record<string, string>
  return {
    ...empty,
    purpose: typeof legacy['purpose'] === 'string' ? legacy['purpose'] : null,
  }
}

/** Builder principal — devuelve el markdown completo del prompt preview
 * que el SectionDraftAgent recibiría para `sectionId` en `locale`. Si la
 * sección no existe, retorna un mensaje localizado. */
export function buildPromptPreview(input: PromptPreviewInput): string {
  const { definition, sectionId, locale } = input
  const h = HEADERS[locale]

  const structural = findStructural(definition.sections, sectionId)
  if (!structural) return h.no_section

  const docBlock = definition.i18n[locale]
  const i18nSection = findI18nSection(definition, sectionId, locale)
  const ai = normalizeAiInstructions(i18nSection?.ai_instructions)
  const docGlobalInstructions = coerceList(docBlock?.ai_global_instructions)

  const parts: string[] = []

  // Reglas generales del documento (bullets de ai_global_instructions).
  if (docGlobalInstructions.length > 0) {
    parts.push(h.doc_rules)
    parts.push(
      ...docGlobalInstructions.map((rule) => `- ${sanitizeBulletItem(rule)}`),
    )
    parts.push('')
  }

  // Propósito del documento (purpose doc-level).
  const docPurpose = docBlock?.purpose?.trim()
  if (docPurpose) {
    parts.push(h.doc_purpose)
    parts.push(docPurpose)
    parts.push('')
  }

  // Tono y audiencia (doc-level).
  const tone = docBlock?.tone_audience?.trim()
  if (tone) {
    parts.push(h.tone_audience)
    parts.push(tone)
    parts.push('')
  }

  // Contexto inyectado de secciones previas (context_sections).
  const ctxRefs = structural.context_sections ?? []
  if (ctxRefs.length > 0) {
    parts.push(h.injected_context)
    for (const ref of ctxRefs) {
      const isCross = ref.includes('.')
      if (isCross) {
        parts.push(`- \`${ref}\` ${h.context_from_section} cross-template`)
      } else {
        const refI18n = findI18nSection(definition, ref, locale)
        const refTitle = refI18n?.title ?? ref
        parts.push(`- \`${ref}\` ${h.context_from_section} «${refTitle}»`)
      }
    }
    parts.push('')
  }

  // Propósito de esta sección (ai_instructions.purpose).
  if (ai.purpose?.trim()) {
    parts.push(h.section_purpose)
    parts.push(ai.purpose.trim())
    parts.push('')
  }

  // Must include (bullets).
  if (ai.must_include.length > 0) {
    parts.push(h.must_include)
    parts.push(
      ...ai.must_include.map((item) => `- ${sanitizeBulletItem(item)}`),
    )
    parts.push('')
  }

  // Must avoid (bullets).
  if (ai.must_avoid.length > 0) {
    parts.push(h.must_avoid)
    parts.push(
      ...ai.must_avoid.map((item) => `- ${sanitizeBulletItem(item)}`),
    )
    parts.push('')
  }

  // Examples.
  if (ai.examples) {
    if (ai.examples.good?.trim()) {
      parts.push(h.example_good)
      parts.push(ai.examples.good.trim())
      parts.push('')
    }
    if (ai.examples.bad?.content?.trim()) {
      parts.push(h.example_bad)
      parts.push(ai.examples.bad.content.trim())
      if (ai.examples.bad.explanation?.trim()) {
        parts.push('')
        parts.push(`${h.why_bad} ${ai.examples.bad.explanation.trim()}`)
      }
      parts.push('')
    }
  }

  // Si el author no declaró nada, mensaje honesto.
  if (parts.length === 0) {
    return h.no_content_declared
  }

  // Trim trailing blank line.
  while (parts.length > 0 && parts[parts.length - 1] === '') parts.pop()
  return parts.join('\n')
}

/** Lista plana de las secciones del documento (recorre children).
 * Usado por el dropdown del PromptsIaTab para construir las opciones. */
export function flattenSections(
  sections: ArtifactStructuralSection[],
): ArtifactStructuralSection[] {
  const out: ArtifactStructuralSection[] = []
  function walk(nodes: ArtifactStructuralSection[]): void {
    for (const n of nodes) {
      out.push(n)
      if (n.children && n.children.length > 0) walk(n.children)
    }
  }
  walk(sections)
  return out
}
